<template>
  <el-skeleton animated class="job-days-skeleton" :class="{ 'is-calendar': layout === 'calendar' }">
    <template #template>
      <div class="job_info_wrapper" v-if="!shiftsOnly">
        <div class="image_text_comp" :class="{ 'd-block': layout !== 'calendar' }">
          <div class="image_wrapper">
            <el-skeleton-item variant="rect" class="avatar-skeleton" />
          </div>
          <div class="text_wrapper">
            <el-skeleton-item variant="text" class="name-skeleton" />
            <el-skeleton-item variant="text" class="distance-skeleton" />
          </div>
        </div>

        <div class="info-separator" />

        <div class="rate_shifts_info">
          <div class="info_wrapper d-flex align-items-center">
            <ClockIcon />
            <el-skeleton-item variant="text" class="meta-skeleton" />
          </div>
          <div class="info_wrapper d-flex align-items-center">
            <LocationIcon />
            <el-skeleton-item variant="text" class="meta-skeleton wide" />
          </div>
          <div class="info_wrapper d-flex align-items-center">
            <DollarWithCircleIcon />
            <el-skeleton-item variant="text" class="meta-skeleton rate-label" />
            <el-skeleton-item variant="text" class="meta-skeleton rate-value" />
          </div>
        </div>

        <div class="collapse-spacing">
          <div class="collapse-skeleton">
            <el-skeleton-item variant="text" class="collapse-title-skeleton" />
            <el-skeleton-item variant="rect" class="collapse-chevron-skeleton" />
          </div>
        </div>
      </div>

      <div class="job_shifts_wrapper">
        <div class="d-flex justify-content-between mb-4 align-items-center">
          <el-skeleton-item variant="text" class="heading-skeleton" />
          <el-skeleton-item variant="rect" class="select-all-skeleton" v-if="layout !== 'calendar'" />
        </div>

        <div class="job_shifts_form_checkbox" v-for="n in count" :key="n">
          <div class="shift-card-skeleton">
            <div class="day_info">
              <el-skeleton-item variant="text" class="day-skeleton" />
              <el-skeleton-item variant="text" class="time-skeleton" />
            </div>
            <div class="info-wrapper">
              <div class="day_info_block" v-for="block in shiftBlocks" :key="block.id">
                <component :is="block.icon" />
                <div class="block-copy">
                  <el-skeleton-item variant="text" class="block-label-skeleton" />
                  <el-skeleton-item variant="text" class="block-value-skeleton" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="button_submit" v-if="layout !== 'calendar'">
          <el-skeleton-item variant="rect" class="action-skeleton" />
          <el-skeleton-item variant="rect" class="action-skeleton" />
        </div>
      </div>
    </template>
  </el-skeleton>
</template>

<script setup>
import { computed, defineProps } from "vue";
import { ElSkeleton, ElSkeletonItem } from "@/components/Vendor/Element";
import ClockIcon from "@/components/Icons/ClockIcon.vue";
import LocationIcon from "@/components/Icons/LocationIcon.vue";
import DollarWithCircleIcon from "@/components/Icons/DollarWithCircleIcon.vue";
import RoutingIcon from "@/components/Icons/RoutingIcon.vue";

const props = defineProps({
  count: {
    type: Number,
    default: 3,
  },
  isUs: {
    type: Boolean,
    default: false,
  },
  shiftsOnly: {
    type: Boolean,
    default: false,
  },
  layout: {
    type: String,
    default: "booking",
  },
});

const shiftBlocks = computed(() => {
  const blocks = [
    { id: "rate", icon: DollarWithCircleIcon },
    { id: "travel", icon: RoutingIcon },
  ];
  if (!props.isUs) {
    blocks.push({ id: "super", icon: DollarWithCircleIcon });
  }
  blocks.push({ id: "sum", icon: DollarWithCircleIcon });
  return blocks;
});
</script>

<style scoped lang="scss">
.job-days-skeleton {
  display: block;
}

.job-days-skeleton.is-calendar {
  .image_text_comp {
    display: flex !important;
    flex-direction: row;
    align-items: center;
    gap: 12px;
  }

  .avatar-skeleton {
    width: 74px;
    height: 74px;
  }

  .name-skeleton {
    width: 180px;
    height: 22px;
  }

  .heading-skeleton {
    width: 110px;
  }
}

.image_text_comp.d-block {
  display: flex !important;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.image_wrapper {
  margin-right: 0;
}

.text_wrapper {
  width: 100%;
  max-height: none;
}

.rate_shifts_info {
  .info_wrapper {
    gap: 8px;

    + .info_wrapper {
      margin-top: 24px;
    }
  }
}

.avatar-skeleton {
  width: 36px;
  height: 36px;
  border-radius: var(--locumate-corner-radius);
}

.name-skeleton {
  display: block;
  width: 168px;
  height: 20px;
}

.distance-skeleton {
  display: block;
  width: 92px;
  height: 14px;
  margin-top: 6px;
}

.meta-skeleton {
  flex: 1;
  max-width: 220px;
  height: 16px;
  margin-left: 0;

  &.wide {
    max-width: 260px;
  }

  &.rate-label {
    flex: 0 1 auto;
    width: 110px;
    max-width: 110px;
  }

  &.rate-value {
    flex: 0 0 auto;
    width: 72px;
    max-width: 72px;
    margin-left: auto;
  }
}

.collapse-spacing {
  margin-block: 20px;
}

.collapse-skeleton {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  padding: 12px 16px;
  border: 1px solid rgba(var(--locumate-clr-gray-light), 0.5);
  border-radius: var(--locumate-corner-radius, 8px);
  background-color: rgba(var(--locumate-secondary-color), 0.12);
}

.collapse-title-skeleton {
  width: 148px;
  height: 16px;
}

.collapse-chevron-skeleton {
  width: 14px;
  height: 14px;
  border-radius: 2px;
}

.heading-skeleton {
  width: 64px;
  height: 18px;
}

.select-all-skeleton {
  width: 128px;
  height: 28px;
  border-radius: 999px;
}

.shift-card-skeleton {
  border: 1px solid rgb(var(--locumate-clr-gray-light));
  border-radius: var(--locumate-corner-radius);
  padding: 12px;
}

.day_info {
  margin-bottom: 2px;
}

.day-skeleton {
  display: block;
  width: 186px;
  height: 20px;
}

.time-skeleton {
  display: block;
  width: 148px;
  height: 15px;
  margin-top: 6px;
}

.info-wrapper {
  display: grid;
  gap: 12px 10px;
  grid-template-columns: 1fr 42%;
  margin-block: 10px;
}

.day_info_block {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.block-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.block-label-skeleton {
  display: block;
  width: 78px;
  height: 12px;
}

.block-value-skeleton {
  display: block;
  width: 56px;
  height: 14px;
}

.button_submit {
  display: flex;
  gap: 12px;
  margin-top: 8px;
}

.action-skeleton {
  flex: 1;
  height: 44px;
  border-radius: var(--locumate-corner-radius);
}

:deep(.el-skeleton__item) {
  display: block;
}

:deep(.avatar-skeleton),
:deep(.collapse-chevron-skeleton),
:deep(.select-all-skeleton),
:deep(.action-skeleton) {
  display: inline-block;
  flex-shrink: 0;
}
</style>
